import React, { Component } from 'react';
import { observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { noop } from 'lodash';

import { Modal, Well } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoan } from '../../../interfaces';
import { Card, IFieldSetPartial } from '../../../lib/mighty-fields';
import { SessionStoreClass } from '../../../stores';
import LoanDocuments from '../common/LoanDocuments';

import LoanTermsTable from './LoanTermsTable';
import { termsAsPercentages } from './terms';

const { PERMISSIONS } = AppConstants;

// Status comes back as a raw enum value; its label lives in the option list served by /options/, as
// the account's own status does, and as the loans table renders it. Nothing here is editable: the
// table's own Edit option is where a loan is changed.
const FIELD_SETS_LOAN_INFO: IFieldSetPartial[] = [
  [
    { field: 'vanity_id', label: 'Loan Id' },
    { field: 'status', optionKey: 'value', optionType: 'loanStatusOptions', type: 'optionSelect' },
    { field: 'origination_date', type: 'date' },
    // Empty while the loan is open, which is what the status beside it already says
    { field: 'closed_date', label: 'Closed', type: 'date' },
  ],
]
  // Principal and interest read off the loan's own two fields rather than out of `balances`, which
  // carries the same pair: principal needs no interest rate, so current_principal_amount is still a
  // real figure on a loan the engine cannot accrue, where every row below it is empty.
  //
  // The rest reach into the nested balances object by path -- CardRow resolves a field through
  // lodash get, so a dotted path works and nothing has to be flattened onto the model first.
  , FIELD_SETS_BALANCE: IFieldSetPartial[] = [
    [
      { field: 'current_principal_amount', label: 'Current Principal', type: 'money' },
      { field: 'current_interest_amount', label: 'Current Interest', type: 'money' },
    ],
    [
      { field: 'balances.total_balance', label: 'Total Balance', type: 'money' },
      // What closing the loan today costs -- above the balance while the minimum period runs.
      { field: 'balances.payoff_amount', label: 'Payoff Today', type: 'money' },
    ],
    [
      { field: 'balances.interest_accrued_to_date', label: 'Interest Accrued to Date', type: 'money' },
      { field: 'balances.interest_paid_to_date', label: 'Interest Paid to Date', type: 'money' },
    ],
  ]
  , FIELD_SETS_CONTRACT: IFieldSetPartial[] = [
    [
      { field: 'contract_template_name', label: 'Contract Template' },
      { field: 'initial_principal_amount', label: 'Initial Principal', type: 'money' },
    ],
    [
      // Served as a fraction of 1, as the terms' own rates are; the percentage type multiplies it
      // back out, so 0.24 reads as 24.00%.
      { field: 'annual_interest_rate', label: 'Annual Interest Rate', type: 'percentage' },
      { field: 'maturity_date', label: 'Maturity Date', type: 'date' },
    ],
  ]
  // Both balloons assume nothing is paid after the last payment logged: the principal figure is what
  // a borrower who services the interest faces at maturity, the balance figure is the whole amount
  // if they pay nothing more between now and then.
  , FIELD_SETS_MATURITY: IFieldSetPartial[] = [
    [
      { field: 'balances.balloon_principal_at_maturity', label: 'Balloon Principal', type: 'money' },
      { field: 'balances.balloon_balance_at_maturity', label: 'Balloon Balance', type: 'money' },
    ],
    [
      { field: 'balances.minimum_period_end_date', label: 'Minimum Period Ends', type: 'date' },
      { field: 'balances.minimum_period_interest_shortfall', label: 'Early Payoff Shortfall', type: 'money' },
    ],
  ]
  ;

interface IProps {
  // The row the table clicked through, which carries the list's fields; the rest is read again below
  loan: ILoan;
  onClose: () => void;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class LoanDetailsModal extends Component<IProps> {
  @observable private isLoading = true;
  @observable private loan: ILoan;
  // Whether a detail payload actually arrived. A failed retrieve leaves `loan` as the list row,
  // which never carries balances by design, so without this the modal cannot tell "the engine
  // cannot price this loan" from "the request failed".
  @observable private hasLoaded = false;

  public constructor (props: IProps) {
    super(props);

    // The list's fields are the ones the modal can show straight away, so it opens on them rather
    // than on an empty card while the rest of the loan is read.
    this.loan = props.loan;
  }

  public componentDidMount () {
    this.fetchLoan();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchLoan () {
    const { Clients } = this.injected;

    this.isLoading = true;
    // Correct on its own terms rather than because LoansTable remounts the modal per row.
    this.hasLoaded = false;

    try {
      // The loans list leaves off the balance and the terms, so the detail view asks for the loan
      // again rather than showing only what the table already had.
      this.loan = await Clients.loans.retrieve(this.props.loan.id);
      this.hasLoaded = true;
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the loan:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  /**
   * In a well of its own, as the lien modal's terms table is, rather than inside the contract card:
   * the table brings its own 'Contract Terms' heading with it.
   *
   * Locked, this view being read only, so the rates render as text and the change handler is never
   * reached. The minimum duration is the loan's own snapshot of it rather than the template's, so a
   * loan reads back under the period it was drawn up with even if the template has been edited
   * since -- which is the whole point of the API keeping a copy.
   */
  private renderTerms () {
    const terms = toJS(this.loan.terms) || [];

    if (!terms.length) { return null; }

    return (
      <Well>
        <LoanTermsTable
          isLocked
          minimumDuration={this.loan.minimum_duration || null}
          onChange={noop}
          terms={termsAsPercentages(terms)}
        />
      </Well>
    );
  }

  /**
   * The documents filed against this loan, as opposed to those filed against the whole account,
   * which are on the account's own Documents tab.
   *
   * Loads itself off the id the table clicked through rather than waiting on fetchLoan, the loan
   * detail carrying nothing it needs. Left off entirely without the view permission -- it is a
   * separate model from the loan, so a user can be allowed one and not the other.
   */
  private renderDocuments () {
    const { SessionStore, Clients } = this.injected;

    if (!SessionStore.userHasPermission(PERMISSIONS.VIEW_LOAN_DOCUMENT)) { return null; }

    return (
      <LoanDocuments
        addPermission={PERMISSIONS.ADD_LOAN_DOCUMENT}
        client={Clients.loanDocuments}
        deletePermission={PERMISSIONS.DELETE_LOAN_DOCUMENT}
        editPermission={PERMISSIONS.EDIT_LOAN_DOCUMENT}
        parent={{ loan: this.props.loan.id }}
        title='Loan Documents'
      />
    );
  }

  /**
   * Every figure that needs an interest rate comes back null on a loan the engine cannot accrue --
   * one with no usable contract terms, or one entered ahead of its origination date. Said once,
   * here, rather than leaving three cards of dashes with no reason given.
   *
   * Only once a detail payload has arrived saying so. While the loan is loading balances is merely
   * not read yet, and on a failed retrieve it is absent rather than null -- claiming the loan has no
   * contract terms would be a confident answer to a question nothing has answered.
   */
  private renderUnaccruedNote () {
    if (!this.hasLoaded || this.loan.balances) { return null; }

    return (
      <Well bsSize='small'>
        Interest cannot be calculated for this loan: it has no usable contract terms, or it has not
        been originated yet. Its principal is still shown above.
      </Well>
    );
  }

  private renderBody () {
    // Each card loads itself rather than the body swapping for one loader: the balance and the terms
    // are not in the row the table clicked through, and a card that is loading shows its heading and
    // a loader in place of its rows -- so the headings stay put and no row is filled with the empty
    // field for the length of the round trip.
    const cardProps = { className: 'space-between', isLoading: this.isLoading, model: this.loan };

    return (
      <div>
        <Card {...cardProps} fieldSets={FIELD_SETS_LOAN_INFO} title='Loan Info' />
        <Card {...cardProps} fieldSets={FIELD_SETS_BALANCE} title='Balance' />
        {this.renderUnaccruedNote()}
        <Card {...cardProps} fieldSets={FIELD_SETS_CONTRACT} title='Contract Details' />
        <Card {...cardProps} fieldSets={FIELD_SETS_MATURITY} title='Maturity &amp; Early Payoff' />
        {this.renderTerms()}
        {this.renderDocuments()}
      </div>
    );
  }

  public render () {
    const { onClose } = this.props;

    return (
      <Modal show className='right side-modal modal-loan-details' onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>Loan Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>{this.renderBody()}</Modal.Body>
      </Modal>
    );
  }
}

export default LoanDetailsModal;
